import classNames from 'classnames';
import type { Delta } from 'quill/core';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useAppDispatch } from '../app/redux/useAppSelector';
import { IconButton } from '../components/Button';
import SVGIcon from '../components/SVGIcon';
import QuillComponent from '../editor/QuillComponent';
import { useQuill } from '../editor/QuillContext';
import SearchOptionsModal from '../help/SearchOptionsModal';
import informationIcon from '../icons/non-prefilled/16x16/information.svg';
import searchIcon from '../icons/non-prefilled/16x16/search.svg';
import { UIActions } from '../ui/uiSlice';
import * as styles from './FacetedSearchInput.module.scss';

interface SearchInputProps {
  placeholder?: string;
  onChange?: (content: Delta) => void;
  onEnter?: () => void;
  initialContent?: Delta;
}

const SearchInput = ({
  placeholder,
  onChange,
  initialContent,
  onEnter,
}: SearchInputProps) => {
  const { quill } = useQuill();
  const handlerRef = useRef({ onEnter });

  useLayoutEffect(() => {
    handlerRef.current = { onEnter };
  }, [onEnter]);

  useEffect(() => {
    if (!quill) return undefined;

    function onKeyDown(e: KeyboardEvent) {
      if (quill?.hasFocus()) {
        if (e.key === 'Escape') {
          quill.blur();
        }

        if (e.key === 'Enter') {
          handlerRef.current.onEnter?.();
        }
      }
    }

    quill.root.addEventListener('keydown', onKeyDown);

    return () => {
      quill.root.removeEventListener('keydown', onKeyDown);
    };
  }, [quill]);

  return (
    <div className={styles.searchInput}>
      <QuillComponent
        moduleConfig={{
          prefixListener: {
            persistOnSelectionChange: true,
          },
          keyboard: {
            bindings: {
              tab: {
                key: 'Tab',
                handler() {
                  return false;
                },
              },
              enter: {
                key: 'Enter',
                shiftKey: null,
                altKey: null,
                handler() {
                  return false;
                },
              },
            },
          },
        }}
        noTitleLine
        namespace="searchInput"
        theme="searchInputEditorTheme"
        readOnly={false}
        contentPlaceholder={placeholder}
        onTextChange={() => {
          if (quill) {
            onChange?.(quill.getContents());
          }
        }}
        initialContent={initialContent}
      />
    </div>
  );
};

interface FacetedSearchInputProps {
  expanded?: boolean;
  placeholder?: string;
  onChange?: (content: Delta) => void;
  onEnter?: () => void;
  content?: Delta;
}

const FacetedSearchInput = ({
  expanded,
  placeholder,
  onChange,
  onEnter,
  content,
}: FacetedSearchInputProps) => {
  const dispatch = useAppDispatch();

  const showSearchOptions = () => {
    dispatch(UIActions.openModal(SearchOptionsModal));
  };

  return (
    <div
      className={classNames(styles.container, {
        [styles.expanded]: expanded,
      })}
    >
      <SVGIcon className={styles.icon}>{searchIcon}</SVGIcon>
      <SearchInput
        placeholder={placeholder}
        onChange={onChange}
        initialContent={content}
        onEnter={onEnter}
      />
      <IconButton
        className={classNames(styles.infoIcon, { [styles.shown]: expanded })}
        onClick={showSearchOptions}
        aria-label="Show search filter options"
      >
        {informationIcon}
      </IconButton>
    </div>
  );
};

export default FacetedSearchInput;
